import React from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Archive, Copy, Edit, Loader2, Megaphone, Pencil, PencilLineIcon, Play, Search } from 'lucide-react'
import dayjs from 'dayjs'
import EmptyState from '../EmptyState'

/**
 * CampaignTable
 *
 * Table component for displaying a list of email campaigns.
 * Used on the Campaigns listing page.
 *
 * Responsibilities:
 * - Display campaign summary information
 * - Handle row navigation to campaign details
 * - Provide action buttons (edit, send, archive, duplicate)
 * - Show sending state for active campaigns
 *
 * Props:
 * @param campaigns Array of campaign objects to display
 * @param handleRowClick Row click handler for navigation to details page
 * @param getStatusColor Utility function to resolve campaign status badge styles
 * @param handleEditCampaign Callback to edit a draft campaign
 * @param handleDuplicateCampaign Callback to duplicate a campaign
 * @param handleSendCampaign Callback to trigger campaign sending
 * @param handleDeleteCampaign Callback to archive a campaign
 *
 * @returns JSX.Element
 */
const CampaignTable = ({
    campaigns,
    handleRowClick,
    getStatusColor,
    handleDetailsCampaign,
    handleEditCampaign,
    handleDuplicateCampaign,
    handleSendCampaign,
    handleDeleteCampaign,
    searchTerm
}: any) => {
    const getUserLabel = (user: any) => {
        if (!user) return "System"
        if (typeof user === "string") return user
        if (typeof user === "object") {
            return user.name || "System"
        }
        return "System"
    }

    /**
     * Formats a date string into a relative time (e.g. "2 hours ago")
     * Falls back to "Unknown" if parsing fails.
     *
     * @param dateString ISO date string
     * @returns Formatted relative time string
     */
    const formatDate = (dateString: string) => {
        try {
            return dayjs(dateString).fromNow()
        } catch {
            return "Unknown"
        }
    }

    return (
        <>
            {campaigns?.length > 0 ? (
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 transition-colors">
                    <Table className="text-xs [&_td]:py-1.5 [&_td]:px-2 [&_th]:py-2 [&_th]:px-2 [&_th]:h-9">
                        <TableHeader className="bg-gray-50 dark:bg-gray-800">
                            <TableRow>
                                <TableHead>Subject</TableHead>
                                <TableHead>Time</TableHead>
                                <TableHead>Sender</TableHead>
                                <TableHead>To</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Failed</TableHead>
                                <TableHead>Success</TableHead>
                                <TableHead>Progress</TableHead>
                                <TableHead className="text-end">Operator</TableHead>
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {campaigns.map((campaign: any) => (
                                <TableRow
                                    key={campaign._id}
                                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                    onClick={() => handleDetailsCampaign(campaign)}
                                >
                                    <TableCell className="font-medium max-w-[150px] md:max-w-[200px] lg:max-w-[250px] break-all whitespace-normal text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400">
                                        <div className="line-clamp-2" title={campaign.name}>
                                            {campaign.name}
                                        </div>
                                    </TableCell>

                                    <TableCell className="text-gray-600 dark:text-gray-300">
                                        {formatDate(campaign?.sentAt)}
                                    </TableCell>

                                    <TableCell className="text-gray-600 dark:text-gray-300">
                                        {getUserLabel(campaign?.createdBy)}
                                    </TableCell>

                                    <TableCell className="text-gray-600 dark:text-gray-300">
                                        {campaign.statistics?.totalRecipients || 0}
                                    </TableCell>

                                    <TableCell>
                                        <Badge className={`text-[10px] px-1.5 py-0 h-5 ${getStatusColor(campaign.status)}`}>
                                            {campaign.status}
                                        </Badge>
                                    </TableCell>

                                    <TableCell className="text-gray-600 dark:text-gray-300">
                                        {campaign.statistics?.emailsFailed || 0}
                                    </TableCell>

                                    <TableCell className="text-gray-600 dark:text-gray-300">
                                        {campaign.statistics?.emailsSent || 0}
                                    </TableCell>

                                    {/* <TableCell className="text-gray-600 dark:text-gray-300">
                                        {campaign.statistics?.clickRate || 0}%
                                    </TableCell> */}

                                    <TableCell className="text-gray-600 dark:text-gray-300">
                                        {Math.floor(campaign.statistics?.progress || 0)}%
                                    </TableCell>

                                    <TableCell data-actions-column onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-1 text-gray-500">

                                            {/* Send */}
                                            {campaign.status === "Draft" && (
                                                <Button
                                                    variant="ghost"
                                                    className="h-6 w-6 p-0"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleSendCampaign(campaign._id)
                                                    }}
                                                    // disabled={campaign?.recipientLists?.length === 0}
                                                    title='Send Campaign'
                                                >
                                                    <Play className="h-3.5 w-3.5 text-gray-700 dark:text-gray-300" />
                                                </Button>
                                            )}

                                            {/* Duplicate */}
                                            {/* {campaign.status === "Draft" && ( */}
                                                <Button
                                                    variant="ghost"
                                                    className="h-6 w-6 p-0"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleDuplicateCampaign(campaign._id)
                                                    }}
                                                    title='Duplicate Campaign'
                                                >
                                                    <Copy className="h-3.5 w-3.5 text-gray-700 dark:text-gray-300" />
                                                </Button>
                                            {/* )} */}

                                            {/* Sending State */}
                                            {campaign.status === "Sending" && (
                                                <div className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                    <span className="text-[10px]">Sending...</span>
                                                </div>
                                            )}

                                            {/* Edit */}
                                            {campaign.status === "Draft" && (
                                                <Button
                                                    variant="ghost"
                                                    className="h-6 w-6 p-0"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleEditCampaign(campaign)
                                                    }}
                                                    title='Edit Campaign'
                                                >
                                                    <PencilLineIcon className="h-3.5 w-3.5 text-gray-700 dark:text-gray-300" />
                                                </Button>
                                            )}

                                            {/* Archive */}
                                            <div
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleDeleteCampaign(campaign._id)
                                                }}
                                                title="Archive Campaign"
                                                className="flex items-center justify-center h-6 w-6 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                                            >
                                                <Archive className="h-3.5 w-3.5 text-gray-700 dark:text-gray-300" />
                                            </div>

                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            ) : (
                <div className="py-16">
                    {searchTerm ? (
                        <EmptyState
                            icon={Search}
                            title="No campaigns found"
                            description="We couldn't find any campaigns matching your search. Try adjusting your search criteria."
                        />
                    ) : (
                        <EmptyState
                            icon={Megaphone}
                            title="No campaigns yet"
                            description="Create your first campaign to start sending emails to your audience."
                        />
                    )}
                </div>

            )}
        </>
    )
}

export default CampaignTable
